import { CopyOutlined, FullscreenOutlined } from '@ant-design/icons';
import { useAppSelector } from '@/store/hooks';
import { Modal, Select, Button, message, Input, Radio } from 'antd';
import { useState, useEffect, useMemo } from 'react';

import { shareCanvas, createArtifact, CreateArtifactResponse } from '@/api/weave-x';
import canvasWebSocket from '@/services/websocket-service';
import {
  selectInvestors,
  selectInvestorsLoadingState,
} from '@/store/features/investors/investorsSlice';
import { getInvestorDisplayName, getInvestorOptionLabel } from '@/util/investor-display';

interface ShareVisualizationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const MAX_CANVAS_TITLE_LENGTH = 100;

const stripUnsafeTitleCharacters = (title: string) =>
  title
    .replace(/<[^>]*>/g, '')
    .replace(/[<>]/g, '')
    .split('')
    .filter((character) => {
      const code = character.charCodeAt(0);
      return code > 31 && code !== 127;
    })
    .join('');

export const validateAndSanitizeCanvasTitle = (
  title: string,
): { title: string; error?: never } | { title?: never; error: string } => {
  const sanitizedTitle = stripUnsafeTitleCharacters(title).trim();

  if (!sanitizedTitle) {
    return { error: 'Please enter a title for the Wavvest Planning' };
  }

  if (sanitizedTitle.length > MAX_CANVAS_TITLE_LENGTH) {
    return { error: `Plan title must be ${MAX_CANVAS_TITLE_LENGTH} characters or fewer` };
  }

  return { title: sanitizedTitle };
};

export function ShareVisualizationModal({ isOpen, onClose }: ShareVisualizationModalProps) {
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  const [shareType, setShareType] = useState<'investor' | 'artifact'>('investor');
  const [artifactData, setArtifactData] = useState<CreateArtifactResponse | null>(null);
  const [isCreatingArtifact, setIsCreatingArtifact] = useState(false);
  const [canvasTitle, setCanvasTitle] = useState<string>('');

  const investors = useAppSelector(selectInvestors);
  const loading = useAppSelector(selectInvestorsLoadingState) === 'pending';
  const { visualization } = useAppSelector((state) => state.canvas);
  const { user } = useAppSelector((state) => state.userinfo);

  const existingInvestors = useMemo(
    () => investors.filter((investor) => investor.cognito_id),
    [investors],
  );

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedUser(null);
      setIsSharing(false);
      setShareType('investor');
      setArtifactData(null);
      setIsCreatingArtifact(false);
      setCanvasTitle('');
    }
  }, [isOpen]);

  const handleUserSelect = (userId: string) => {
    setSelectedUser(userId);
  };

  const handleCreateArtifact = async () => {
    if (!visualization.sourceCode) {
      message.error('No plan available to share');
      return;
    }

    const sessionId = canvasWebSocket.getSessionId();
    if (!sessionId) {
      message.error('Session ID not found, please refresh your page');
      return;
    }

    try {
      setIsCreatingArtifact(true);

      const result = await createArtifact({
        session_id: sessionId,
        htmlSourceCode: visualization.sourceCode,
      });

      if (result && result.success) {
        setArtifactData(result);
        message.success('Artifact link created successfully');
      } else {
        message.error('Failed to create artifact link');
      }
    } catch (error) {
      console.error('Error creating artifact:', error);
      message.error('Failed to create artifact link');
    } finally {
      setIsCreatingArtifact(false);
    }
  };

  const handleCopyLink = async () => {
    if (artifactData?.link) {
      try {
        await navigator.clipboard.writeText(artifactData.link);
        message.success('Link copied to clipboard');
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        message.error('Failed to copy link');
      }
    }
  };

  const handleOpenLink = () => {
    if (artifactData?.link) {
      window.open(artifactData.link, '_blank', 'noopener,noreferrer');
    }
  };

  const handlePreviewLink = () => {
    if (artifactData?.link) {
      window.open(artifactData.link, '_blank', 'noopener,noreferrer,width=800,height=600');
    }
  };

  const handleShare = async () => {
    if (!visualization.sourceCode || !user) {
      message.error('No plan available to share');
      return;
    }

    if (!selectedUser) {
      message.error('Please select an investor to share with');
      return;
    }

    const titleValidation = validateAndSanitizeCanvasTitle(canvasTitle);
    if (titleValidation.error) {
      message.error(titleValidation.error);
      return;
    }

    const sessionId = canvasWebSocket.getSessionId();
    if (!sessionId) {
      message.error('Session ID not found, please refresh your page');
      return;
    }

    try {
      setIsSharing(true);

      const result = await shareCanvas({
        userId: user.id,
        sessionId: sessionId,
        title: titleValidation.title,
        visualizationId: 'current',
        visualizationHtml: {
          sourceCode: visualization.sourceCode,
          fileName: visualization.fileName,
          presignedUrl: visualization.presignedUrl,
        },
        sharedWith: [selectedUser],
      });

      if (result) {
        message.success('Plan shared successfully');
        onClose();
      } else {
        message.error('Failed to share plan');
      }
    } catch (error) {
      console.error('Error sharing plan:', error);
      message.error('Failed to share plan');
    } finally {
      setIsSharing(false);
    }
  };

  const getModalFooter = () => {
    if (shareType === 'artifact') {
      if (artifactData) {
        return [
          <Button key="close" onClick={onClose}>
            Close
          </Button>,
        ];
      } else {
        return [
          <Button key="cancel" onClick={onClose}>
            Cancel
          </Button>,
          <Button
            key="create"
            type="primary"
            onClick={handleCreateArtifact}
            loading={isCreatingArtifact}
          >
            Generate Link
          </Button>,
        ];
      }
    } else {
      return [
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="share"
          type="primary"
          onClick={handleShare}
          loading={isSharing}
          disabled={!selectedUser || !canvasTitle.trim()}
        >
          Share
        </Button>,
      ];
    }
  };

  return (
    <Modal title="Share Plan" open={isOpen} onCancel={onClose} footer={getModalFooter()}>
      <div className="share-visualization-content">
        <div style={{ marginBottom: 16 }}>
          <Radio.Group value={shareType} onChange={(e) => setShareType(e.target.value)}>
            <Radio value="investor">
              <strong>Share with Investor</strong>
            </Radio>
            <Radio value="artifact">
              <strong>Create Public Link</strong>
            </Radio>
          </Radio.Group>
        </div>

        {shareType === 'investor' ? (
          <div>
            <div style={{ marginBottom: 16 }}>
              <h4>Plan Title:</h4>
              <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                Give your plan a descriptive title that will help identify it when shared.
              </p>
              <Input
                placeholder="Enter a title for this plan"
                value={canvasTitle}
                onChange={(e) => setCanvasTitle(e.target.value)}
                maxLength={MAX_CANVAS_TITLE_LENGTH}
                style={{ width: '100%' }}
              />
            </div>
            <h4>Select an investor to share this plan with:</h4>
            <Select
              placeholder="Select an investor"
              style={{ width: '100%' }}
              loading={loading}
              onChange={handleUserSelect}
              value={selectedUser}
              optionFilterProp="label"
              showSearch
            >
              {existingInvestors.map((investor) => {
                const displayName = getInvestorDisplayName(investor);

                return (
                  <Select.Option
                    key={investor.cognito_id}
                    value={investor.cognito_id}
                    label={getInvestorOptionLabel(investor)}
                  >
                    <div className="user-option">
                      <div className="user-name">{displayName}</div>
                      <div className="user-email">{investor.email}</div>
                    </div>
                  </Select.Option>
                );
              })}
            </Select>
          </div>
        ) : (
          <div>
            {!artifactData ? (
              <div>
                <p>
                  Generate a shareable link for your artifact that others can view without needing
                  to log in.
                </p>
                <p>Click "Generate Link" to create a public URL for this visualization.</p>
              </div>
            ) : (
              <div>
                <div style={{ marginBottom: 16 }}>
                  <strong>Your artifact link has been created!</strong>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <Input
                    value={artifactData.link}
                    readOnly
                    addonAfter={
                      <Button icon={<CopyOutlined />} onClick={handleCopyLink} size="small">
                        Copy
                      </Button>
                    }
                  />
                </div>

                <div style={{ display: 'flex', gap: '8px', marginBottom: 16 }}>
                  <Button onClick={handlePreviewLink} icon={<FullscreenOutlined />}>
                    Preview
                  </Button>
                  <Button onClick={handleOpenLink} type="primary">
                    Open Link
                  </Button>
                </div>

                <div style={{ fontSize: '12px', color: '#666' }}>
                  <div>
                    <strong>Artifact ID:</strong> {artifactData.artifactId}
                  </div>
                  <div>
                    <strong>Created:</strong> {new Date(artifactData.createdAt).toLocaleString()}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
